import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, LogOut, Menu, Monitor, Moon, Search, Sun, UserCircle } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/status-pill';
import { NotificationBell } from './NotificationBell';
import { useTheme, type ThemePreference } from '@/app/theme';
import { humanise } from '@/lib/format';

const THEMES: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

export function Topbar({
  onOpenNav,
  sidebarMinimised,
  onToggleSidebar,
}: {
  onOpenNav: () => void;
  sidebarMinimised: boolean;
  onToggleSidebar: () => void;
}) {
  const { user, logout } = useAuth();
  const { preference, setPreference } = useTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const term = search.trim();
    if (term) navigate(`/customers?search=${encodeURIComponent(term)}`);
  };

  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-slate-200 bg-surface px-3 sm:px-4">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onOpenNav}
        aria-label="Open navigation menu"
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </Button>
      {/* Desktop: the same burger minimises the sidebar to an icon rail rather than opening a drawer. */}
      <Button
        variant="ghost"
        size="icon"
        className="hidden lg:inline-flex"
        onClick={onToggleSidebar}
        aria-label={sidebarMinimised ? 'Expand sidebar' : 'Minimise sidebar'}
        aria-pressed={sidebarMinimised}
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
      </Button>

      <form onSubmit={submitSearch} className="relative min-w-0 flex-1 sm:max-w-sm" role="search">
        <label htmlFor="global-search" className="sr-only">
          Search customers
        </label>
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
          aria-hidden="true"
        />
        <Input
          id="global-search"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search customers…"
          className="pl-9"
        />
      </form>

      <div className="ml-auto flex items-center gap-1">
        <NotificationBell />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button variant="ghost" className="gap-2">
              <UserCircle className="h-5 w-5 text-slate-400" aria-hidden="true" />
              <span className="hidden max-w-[10rem] truncate text-sm sm:inline">
                {user?.fullName ?? 'Account'}
              </span>
              <ChevronDown className="h-4 w-4 text-slate-400" aria-hidden="true" />
            </Button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-[14rem] rounded-md border border-slate-200 bg-surface p-1 shadow-lg"
            >
              <div className="px-2 py-2">
                <p className="truncate text-sm font-medium text-slate-900">{user?.fullName}</p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
                <Badge tone={user?.role === 'ADMIN' ? 'info' : 'neutral'} className="mt-1.5">
                  {humanise(user?.role)}
                </Badge>
              </div>

              <DropdownMenu.Separator className="my-1 h-px bg-slate-200" />

              {/*
               * A radio group rather than a toggle: with three states, a switch cannot show
               * which one is active, and "System" is not the absence of a choice.
               */}
              <DropdownMenu.Label className="px-2 pb-1 pt-1.5 text-xs font-medium text-slate-500">
                Appearance
              </DropdownMenu.Label>
              <DropdownMenu.RadioGroup
                value={preference}
                onValueChange={(next) => setPreference(next as ThemePreference)}
              >
                {THEMES.map((theme) => (
                  <DropdownMenu.RadioItem
                    key={theme.value}
                    value={theme.value}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-slate-700 outline-none data-[highlighted]:bg-slate-100 data-[state=checked]:font-medium data-[state=checked]:text-brand-700"
                  >
                    <theme.icon className="h-4 w-4" aria-hidden="true" />
                    {theme.label}
                  </DropdownMenu.RadioItem>
                ))}
              </DropdownMenu.RadioGroup>

              <DropdownMenu.Separator className="my-1 h-px bg-slate-200" />

              <DropdownMenu.Item
                onSelect={() => void logout()}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-slate-700 outline-none data-[highlighted]:bg-slate-100"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
